import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { LOGO_URL } from "@/utils/constants";

export const SITE_NAME = "Wellness Collective AI";
const DEFAULT_TITLE = `${SITE_NAME} | Discover, Connect & Thrive`;

export const siteOrigin = () => process.env.REACT_APP_PUBLIC_SITE_URL?.replace(/\/$/, "") || (typeof window !== "undefined" ? window.location.origin : "");
export const absoluteUrl = (path) => `${siteOrigin()}${path}`;
export const defaultOgImage = () => absoluteUrl("/og-default.png");

export function pageTitle(title) {
  if (!title) return DEFAULT_TITLE;
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
}

export function trimDescription(text, limit = 158) {
  const compact = String(text || "").replace(/\s+/g, " ").trim();
  if (compact.length <= limit) return compact;
  const cut = compact.slice(0, limit).replace(/\s+\S*$/, "").replace(/[\s,;:—-]+$/, "");
  return `${cut}…`;
}

export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map(([name, path], index) => ({ "@type": "ListItem", position: index + 1, name, item: absoluteUrl(path) })),
  };
}

// react-helmet-async v3 drops <meta> tags nested in fragments when direct <meta> children exist, so build a flat list.
function metaTags({ description, fullTitle, image, canonical, noindex, type }) {
  const tags = [
    { name: "description", content: description },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: "en_US" },
    { property: "og:url", content: canonical },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "robots", content: noindex ? "noindex, nofollow" : "" },
  ];
  return tags.filter((tag) => tag.content);
}

// React 19 hoists Helmet tags natively and leaves server/static head tags in place, so drop the static set once the page owns its metadata.
function useRemoveStaticHeadTags() {
  useEffect(() => {
    document.querySelectorAll('head [data-seo="static"]').forEach((node) => node.remove());
  }, []);
}

export function Seo({ title, description, path, noindex = false, jsonLd, image, type = "website" }) {
  useRemoveStaticHeadTags();
  const fullTitle = pageTitle(title);
  const canonical = path != null ? absoluteUrl(path) : undefined;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Helmet>
      <title>{fullTitle}</title>
      {metaTags({ description, fullTitle, image: image || defaultOgImage(), canonical, noindex, type }).map((tag) => <meta key={tag.name || tag.property} {...tag} />)}
      {canonical && !noindex ? <link rel="canonical" href={canonical} /> : null}
      {schemas.map((schema, index) => <script key={schema["@type"] || index} type="application/ld+json">{JSON.stringify(schema)}</script>)}
    </Helmet>
  );
}

export function SiteSchema() {
  const origin = siteOrigin();
  const verification = (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "").trim();
  return (
    <Helmet>
      {verification ? <meta name="google-site-verification" content={verification} /> : null}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": `${origin}/#organization`,
            "name": SITE_NAME,
            "url": origin,
            "logo": LOGO_URL,
            "description": "A trusted local wellness discovery and real-world connection platform — 28 states + DC, coast to coast.",
          },
          {
            "@type": "WebSite",
            "@id": `${origin}/#website`,
            "name": SITE_NAME,
            "url": origin,
            "publisher": { "@id": `${origin}/#organization` },
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${origin}/?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
        ],
      })}</script>
    </Helmet>
  );
}
